import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MessageSquare, Edit } from 'lucide-react';
import { ReactNode } from 'react';
import type { CRMFollowUp, CRMInteraction } from '@/lib/crm/types';

interface CalendarDateMenuProps {
  trigger: ReactNode;
  onAddInteraction: () => void;
  onAddFollowUp: () => void;
  onEditEvent?: (event: { type: 'interaction' | 'follow-up'; data: any }) => void;
  existingEvents?: {
    followUps: CRMFollowUp[];
    interactions: CRMInteraction[];
  };
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CalendarDateMenu({ 
  trigger,
  onAddInteraction, 
  onAddFollowUp,
  onEditEvent,
  existingEvents,
  open,
  onOpenChange
}: CalendarDateMenuProps) {
  const hasEvents = existingEvents && 
    (existingEvents.followUps.length > 0 || existingEvents.interactions.length > 0);
  
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="space-y-2">
          {/* Show existing events first if any */}
          {hasEvents && (
            <div className="border-b pb-2 mb-2">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Sự kiện ngày này:
              </p>
              
              {/* Follow-ups */}
              {existingEvents.followUps.map(f => (
                <Button
                  key={f.id}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start mb-1"
                  onClick={() => onEditEvent?.({ type: 'follow-up', data: f })}
                >
                  <Badge variant="default" className="mr-2 text-xs">F</Badge>
                  <span className="truncate text-xs flex-1 text-left">
                    {f.notes || 'Follow-up (không có ghi chú)'}
                  </span>
                  <Edit className="ml-auto h-3 w-3" />
                </Button>
              ))}
              
              {/* Interactions */}
              {existingEvents.interactions.map(i => (
                <Button
                  key={i.id}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start mb-1"
                  onClick={() => onEditEvent?.({ type: 'interaction', data: i })}
                >
                  <Badge variant="secondary" className="mr-2 text-xs">I</Badge>
                  <span className="truncate text-xs flex-1 text-left">{i.subject}</span>
                  <Edit className="ml-auto h-3 w-3" />
                </Button>
              ))}
            </div>
          )}
          
          {/* Add new buttons */}
          <div className="space-y-1">
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={onAddInteraction}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              <span>Ghi nhận Tương tác</span>
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={onAddFollowUp}
            >
              <Calendar className="mr-2 h-4 w-4" />
              <span>Thêm Follow-up</span>
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
